<template>
    <div class="tracking-search" :class="{ 'ts-panel': !compact }">
        <div v-if="!compact" class="ts-intro">
            <label class="form-label" :for="inputId">Tracking numbers</label>
            <p class="ts-help text-sm text-color-low">Enter one number per line to check several shipments at once.</p>
            <p class="ts-zh">每行一个单号,可同时查询多个。</p>
        </div>
        <label v-else class="ts-visually-hidden" :for="inputId">Air cargo tracking numbers, one per line</label>
        <textarea
            :id="inputId"
            v-model="trackingNumbers"
            class="form-input ts-input"
            rows="5"
            placeholder="One tracking number per line 每行一个单号"></textarea>
        <p v-if="validationError" class="ts-message text-sm text-color-error" role="alert">{{ validationError }}</p>
        <div v-if="historyNumber.length" class="ts-history">
            <span class="ts-history-label text-xs text-color-low">Recent 最近查询</span>
            <span v-for="(item, index) in historyNumber" :key="item" class="ts-chip">
                <button type="button" class="ts-chip-no" :title="'Add ' + item + ' to the search box'" @click="fillTrackNo(item)">{{ item }}</button>
                <button type="button" class="ts-chip-remove" :aria-label="'Remove ' + item + ' from search history'" @click="deleteTrackNo(index)">&times;</button>
            </span>
        </div>
        <div class="ts-actions">
            <c-button type="button" color="primary" wide-mobile :loading="loading" :disabled="loading" @click="searchNumbers">
                Search
            </c-button>
        </div>
        <div v-if="failed.length" class="ts-failed" role="alert">
            <p v-for="f in failed" :key="f.no" class="ts-failed-line text-sm text-color-error">{{ f.no }} &mdash; {{ f.error }}</p>
        </div>
        <c-accordion v-if="tracks.length" class="ts-results">
            <c-accordion-item v-for="(item, i) in tracks" :key="item.no" :title="item" :active="i === 0">
                <p v-for="(t, index) in item.traces" :key="index" class="ts-trace">[{{ t.time }}] {{ t.description }}</p>
            </c-accordion-item>
        </c-accordion>
    </div>
</template>

<script>
import CButton from '@/components/elements/Button.vue'
import CAccordion from '@/components/elements/Accordion.vue'
import CAccordionItem from '@/components/elements/AccordionItemForTracks.vue'

const HISTORY_KEY = 'historyNumber'
const HISTORY_LIMIT = 10

export default {
  name: 'CTrackingSearch',
  components: {
    CButton,
    CAccordion,
    CAccordionItem
  },
  props: {
    // hides the intro label block (and card border) so the widget can sit inside the homepage hero
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      trackingNumbers: '',
      historyNumber: [],
      tracks: [],
      failed: [],
      loading: false,
      validationError: ''
    }
  },
  computed: {
    inputId() {
      return 'tracking-input-' + this._uid
    }
  },
  watch: {
    trackingNumbers() {
      this.validationError = ''
    }
  },
  mounted() {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY))
      if (Array.isArray(stored)) {
        this.historyNumber = stored.slice(0, HISTORY_LIMIT)
      }
    } catch (e) {
      // localStorage unavailable or corrupted — start with an empty history
    }
  },
  methods: {
    searchNumbers() {
      // one number per line; trim and dedupe
      const numbers = []
      this.trackingNumbers.split(/[\n\r]+/).forEach(item => {
        const no = item.trim()
        if (no !== '' && numbers.indexOf(no) === -1) {
          numbers.push(no)
        }
      })
      if (numbers.length === 0) {
        this.validationError = 'Please enter at least one tracking number. 请输入至少一个单号。'
        return
      }

      this.validationError = ''
      this.loading = true
      this.tracks = []
      this.failed = []
      // the track API takes a single number, so query each one in parallel;
      // a 404 body carries an `error` field explaining why that number failed
      Promise.all(numbers.map(no =>
        this.$axios.get('https://air.line-1.com/api/track', {
          params: { no: no }
        }).then(res => ({ no: no, data: res.data }))
          .catch(err => ({
            no: no,
            error: (err.response && err.response.data && err.response.data.error) || 'Network error, please try again later'
          }))
      )).then(results => {
        results.forEach(r => {
          if (r.data) {
            this.tracks.push(Object.assign({ no: r.no, traces: [] }, r.data))
          } else {
            this.failed.push(r)
          }
        })
      }).finally(() => {
        this.loading = false
      })

      this.saveHistory(numbers)
    },
    saveHistory(numbers) {
      // most recent first, no duplicates, capped
      const kept = this.historyNumber.filter(no => numbers.indexOf(no) === -1)
      this.historyNumber = numbers.concat(kept).slice(0, HISTORY_LIMIT)
      this.persistHistory()
    },
    fillTrackNo(no) {
      const current = this.trackingNumbers
      if (current === '' || /[\n\r]\s*$/.test(current)) {
        this.trackingNumbers = current + no + '\n'
      } else {
        this.trackingNumbers = current + '\n' + no + '\n'
      }
    },
    deleteTrackNo(index) {
      this.historyNumber.splice(index, 1)
      this.persistHistory()
    },
    persistHistory() {
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(this.historyNumber))
      } catch (e) {
        // localStorage unavailable — history just won't persist
      }
    }
  }
}
</script>

<style scoped>
.ts-panel {
    border: 1px solid rgba(128, 128, 128, .3);
    border-radius: 6px;
    padding: 20px 24px;
    text-align: left;
}
.ts-intro {
    margin-bottom: 12px;
}
.ts-help {
    margin: 4px 0 0;
}
.ts-zh {
    font-size: 13px;
    opacity: .7;
    margin: 4px 0 0;
}
.ts-input {
    resize: none;
    width: 100%;
}
.ts-message {
    margin: 8px 0 0;
}
.ts-history {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
}
.ts-chip {
    display: inline-flex;
    align-items: center;
    background: #E3EDFB;
    color: #14479A;
    border-radius: 999px;
    font-size: 13px;
    line-height: 20px;
}
.ts-chip-no,
.ts-chip-remove {
    background: none;
    border: 0;
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    cursor: pointer;
}
.ts-chip-no {
    padding: 2px 2px 2px 12px;
}
.ts-chip-remove {
    padding: 2px 10px 2px 6px;
    font-size: 15px;
}
.ts-chip-remove:hover,
.ts-chip-remove:focus {
    color: #E5484D;
}
.ts-actions {
    margin-top: 16px;
}
.ts-failed {
    margin-top: 16px;
}
.ts-failed-line {
    margin: 0 0 4px;
}
.ts-results {
    margin-top: 16px;
    text-align: left;
}
.ts-trace {
    margin: 0 0 8px;
}
.ts-visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
